import PropTypes from 'prop-types';
import { useRef } from 'react';

import classnames from 'classnames';
import { CRUISE_DETAIL_MODAL, DEFAULT, FILTERS_MODAL } from 'components/Scroller/constants';
import ScrollerModule from 'components/Scroller/ScrollerModule';
import { SHIP } from 'contexts/CruiseSearchContext/constants';
import { useConfig } from 'hooks';
import Image from 'next/image';
import useAnimationFilterCardShip from './animations';

import { Card, CardImg, CardWrapper, ImageGradient, Label } from './components';

import sizes from 'styles/sizes';

const imgSize = sizes.img.small;

const parsedTitle = (title) => {
	const [first, ...rest] = title.split(' ');
	return (
		<div>
			<span>{first}</span>
			{rest.join(' ')}
		</div>
	);
};

/**
 * Used in Ships filter modal. Displays images of allowed ships with basic information. Each image is a button allowing the user to select different ships to filter search results by.
 *
 * @param {number} index
 * @param {func} updateSelection
 * @param {bool} selected
 * @param {bool} storybook - Tell component to use mock data for Storybook.
 * @param {string} scrollerId - ID of parent Scroller
 * @param {object} option - Data on current Destination from the graph. See PropTypes for shape.
 */

const ShipCard = ({ option, index, updateSelection, selected, scrollerId, storybook, disabled }) => {
	const shipRef = useRef();
	const { imageBaseUrl } = useConfig();
	const domain = imageBaseUrl ?? '';
	const backgroundImage = `${domain}${option?.media?.images[0]?.path}?${imgSize}`;

	const { onUpdate } = useAnimationFilterCardShip({
		shipRef,
		index,
	});

	const cardClasses = classnames({
		disabled,
		selected,
		storybook,
	});

	return (
		<ScrollerModule id={`${SHIP}-${option.code}`} onUpdate={onUpdate} scrollerId={scrollerId}>
			<CardWrapper className={cardClasses}>
				<Card id={`ship-card-${option.code}`} ref={shipRef} data-code={option.code} onClick={updateSelection}>
					<CardImg>
						<Image
							src={backgroundImage}
							objectPosition="center"
							layout="fill"
							sizes="50vw"
							quality={50}
							objectFit="cover"
							priority={true}
							unoptimized={true}
							alt={option.label || option.code}
						/>
					</CardImg>
					<ImageGradient ariaIgnore />
					<Label>{parsedTitle(option.label || option.code)}</Label>
				</Card>
			</CardWrapper>
		</ScrollerModule>
	);
};

ShipCard.propTypes = {
	index: PropTypes.number.isRequired,
	option: PropTypes.shape({
		__typename: PropTypes.string.isRequired,
		code: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		media: PropTypes.shape({
			__typename: PropTypes.string.isRequired,
			images: PropTypes.arrayOf(
				PropTypes.shape({
					__typename: PropTypes.string.isRequired,
					path: PropTypes.string.isRequired,
				}).isRequired,
			).isRequired,
		}).isRequired,
	}).isRequired,
	updateSelection: PropTypes.func,
	selected: PropTypes.bool,
	scrollerId: PropTypes.oneOf([DEFAULT, FILTERS_MODAL, CRUISE_DETAIL_MODAL]).isRequired,
	storybook: PropTypes.bool,
	disabled: PropTypes.bool,
};

export default ShipCard;
